import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { apiErrorHandling } from 'src/api/apierrorhandling';
import { getMangoPayAccountInformation } from 'src/api/pmApi';
import { getPayoutsBySpv, getSPVs } from 'src/api/stoApi';
import { SiteHeader } from 'src/components/ui/SiteHeader';
import { SPV } from 'src/models';
import { PayoutStatus } from 'src/models/enum';
import { Routes } from 'src/router/Routes';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Button, Card, Chip, Skeleton, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';

interface SpvWithAccountData extends SPV {
  accountInfo: {
    balance: number;
    IBAN: string;
    frozenBalance?: number;
  };
}

export const AccountsOverview = () => {
  const { t } = useTranslation();
  const [showSkeleton, setShowSkeleton] = useState(true);

  const fetchSpvAndAccountBalance = () => {
    const [spvArray, setSpvArray] = useState<SpvWithAccountData[] | null>(null);

    useEffect(() => {
      const getAccountsAndBalance = async () => {
        setShowSkeleton(true);
        try {
          // Get SPVs associated with this user / partner
          const svpResponse = await getSPVs();
          const spvData = svpResponse.data.data as SpvWithAccountData[];
          setSpvArray(spvData);
          // get pending or init payouts for each spv to calculate how much of the balance is actually available
          const spvIds = spvData.map((spv) => spv.id);
          const payoutResponse = await getPayoutsBySpv(spvIds, [PayoutStatus.init, PayoutStatus.pending]);
          const payoutData = payoutResponse.data.data;
          // get account information for each spv
          if (spvData) {
            for (const [index, spv] of spvData?.entries()) {
              try {
                // Get mango pay balance and account information
                const mangoPay = await getMangoPayAccountInformation(spv.paymentProvider.mangopayId, spv.paymentProvider.mangopayWalletId);
                spvData[index].accountInfo = mangoPay.data.data;
                // find all payouts for this spv, sum them up and add to .frozenBalance in the accountInfo object
                const payoutsForSpv = payoutData[spv.id];
                const frozenBalance = payoutsForSpv.reduce(
                  (acc: number, payout: any) => acc + Number(payout.payoutAmount.$numberDecimal),
                  0,
                );
                spvData[index].accountInfo.frozenBalance = frozenBalance;
                setSpvArray(spvData);
              } catch (error) {
                apiErrorHandling(error);
              }
            }
          }
          console.log(spvData);
        } catch (error: any) {
          apiErrorHandling(error);
        } finally {
          setShowSkeleton(false);
        }
      };
      getAccountsAndBalance();
    }, []);

    return spvArray;
  };

  const accountInfo = fetchSpvAndAccountBalance();
  const waitForIban = (spv: SpvWithAccountData) => {
    if (showSkeleton) return <Skeleton />;
    if (spv?.accountInfo?.IBAN) {
      return spv.accountInfo.IBAN;
    } else {
      return t('payment.overview.noIban');
    }
  };

  const waitForBalance = (spv: SpvWithAccountData) => {
    if (showSkeleton) return <Skeleton />;
    if (spv?.accountInfo?.balance) {
      return '€ ' + spv.accountInfo.balance;
    } else {
      return t('payment.overview.noBalance');
    }
  };

  const calculateAvailableBalance = (spv: SpvWithAccountData) => {
    if (showSkeleton) return <Skeleton />;
    let balance = 0;
    if (spv?.accountInfo?.balance && spv?.accountInfo?.frozenBalance) {
      balance = spv.accountInfo.balance - spv.accountInfo.frozenBalance;
      return t('payment.overview.availableAmount') + ' ' + balance.toFixed(2);
    } else if (!spv?.accountInfo?.balance) {
      return t('payment.overview.noBalance');
    } else if (!spv?.accountInfo?.frozenBalance) {
      return t('payment.overview.availableAmount') + ' ' + spv?.accountInfo?.balance;
    }
  };
  return (
    <>
      <SiteHeader
        breadcrumbs={[
          {
            link: Routes.PAYMENT.ROOT,
            label: t('payment.breadcrumbs.payment'),
            icon: <AccountBalanceIcon sx={{ mr: 0.5 }} fontSize="inherit" />,
          },
        ]}
        headline={t('payment.overview.title')}
      />
      <Card sx={{ p: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 500, mb: 3 }}>
          {t('payment.overview.accounts')}
        </Typography>

        {accountInfo &&
          accountInfo.map((spv) => (
            <Grid container spacing={2} sx={{ pl: 2 }} key={spv.id}>
              <Grid xs={4}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {spv.companyName}
                </Typography>
                <Typography variant="body1" sx={{ color: 'GrayText' }}>
                  {waitForIban(spv)}
                </Typography>
                {spv.mangopayReadiness?.isReady ? (
                  <Chip icon={<CheckCircleIcon />} label={t('payment.overview.sca.ready')} color="success" size="small" sx={{ mt: 0.5 }} />
                ) : (
                  <Chip
                    icon={<CancelIcon />}
                    label={t('payment.overview.sca.actionRequired')}
                    color="warning"
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                )}
              </Grid>
              <Grid xs={3}>
                <Typography variant="body1" sx={{ fontWeight: 500 }} textAlign="end">
                  {waitForBalance(spv)}
                </Typography>
                <Typography variant="body1" sx={{ color: 'GrayText' }} textAlign="end">
                  {calculateAvailableBalance(spv)}
                </Typography>
              </Grid>
              <Grid>
                <Button variant="contained" to={Routes.PAYMENT.ACCOUNT.replace(':spvId', spv.id)} component={Link}>
                  {t('payment.overview.button.accountOverview')}
                </Button>
              </Grid>
              <Grid>
                <Button variant="contained" to={Routes.PAYMENT.RENT.replace(':spvId', spv.id)} component={Link}>
                  {t('payment.overview.button.rentPayment')}
                </Button>
              </Grid>
            </Grid>
          ))}
      </Card>
    </>
  );
};
